/* eslint-disable import/no-cycle */
import { Parsers, Formatters, DayOfWeekNames, LocaleData } from '../../gen-src/Localize';
import { DateFormat } from '../widgets/DateInputField';

export enum DateTerminus {
  Start,
  End,
}

export enum DayOfWeek {
  Sun,
  Mon,
  Tue,
  Wed,
  Thu,
  Fri,
  Sat,
}

export function getDayOfWeekEnum(dayOfWeek: keyof DayOfWeekNames): DayOfWeek {
  switch (dayOfWeek) {
    case 'sun':
      return DayOfWeek.Sun;
    case 'mon':
      return DayOfWeek.Mon;
    case 'tue':
      return DayOfWeek.Tue;
    case 'wed':
      return DayOfWeek.Wed;
    case 'thu':
      return DayOfWeek.Thu;
    case 'fri':
      return DayOfWeek.Fri;
    case 'sat':
      return DayOfWeek.Sat;
    default:
      throw new Error(`invalid DayOfWeekNames ${dayOfWeek}`);
  }
}

/*  Returns the date of the past offset days or future offset days. */
export function getDateWithDayOffset(date: Date, offset: number): Date {
  const newDate = new Date(date.getTime());
  newDate.setDate(date.getDate() + offset);
  return newDate;
}

/*
  Given a date, returns another date that is on the same day but in the past offset
  month or future offset month, if such day doesn't exist, returns the closest day
  in the past offset month or future offset month.
 */
export function getDateWithMonthOffset(date: Date, offset: number): Date {
  const newDate = new Date(date.getTime());
  const newMonth = date.getMonth() + offset;
  newDate.setMonth(newMonth);
  if (newDate.getDate() !== date.getDate()) {
    newDate.setDate(0);
  }
  return newDate;
}

/*
  Given a date, returns another date that is on the same day and month, but in the past
  offset year or future offset year, if such day doesn't exist, returns the closest day
  in the past offset year or future offset year.
 */
export function getDateWithYearOffset(date: Date, offset: number): Date {
  const newDate = new Date(date.getTime());
  const newYear = date.getFullYear() + offset;
  newDate.setFullYear(newYear);
  if (newDate.getMonth() !== date.getMonth()) {
    newDate.setDate(0);
  }
  return newDate;
}

/*
  Returns the date of the first day of current display (which might be
  different from the first day of current month).
*/
export function getFirstDisplayedDate(date: Date, firstDayOfWeek: DayOfWeek): Date {
  const newDate = new Date(date.getTime());
  newDate.setDate(1);
  if (newDate.getDay() < firstDayOfWeek) {
    newDate.setDate(newDate.getDate() - newDate.getDay() - 7 + firstDayOfWeek);
  } else {
    newDate.setDate(newDate.getDate() - newDate.getDay() + firstDayOfWeek);
  }
  return newDate;
}

/* Returns whether two date objects have the same date, ignore time */
export function areSameDates(date1: Date, date2?: Date): boolean {
  return date1.getFullYear() === date2?.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate();
}

/* Returns the date of the first day of this month */
export function getFirstDayOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

/* Returns the date of the last day of this month */
export function getLastDayOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

/* Returns the date of the first day of this week */
export function getFirstDayOfWeek(date: Date, firstDayOfWeek: DayOfWeek): Date {
  const newDate = new Date(date.getTime());
  if (newDate.getDay() < firstDayOfWeek) {
    newDate.setDate(newDate.getDate() - newDate.getDay() - 7 + firstDayOfWeek);
  } else {
    newDate.setDate(newDate.getDate() - newDate.getDay() + firstDayOfWeek);
  }
  return newDate;
}

/* Returns the date of the last day of this week */
export function getLastDayOfWeek(date: Date, firstDayOfWeek: DayOfWeek): Date {
  const newDate = new Date(date.getTime());
  newDate.setDate(getFirstDayOfWeek(date, firstDayOfWeek).getDate() + 6);
  return newDate;
}

export function getLocalizedDaysOfWeek(firstDayOfWeek: DayOfWeek): string[] {
  // July 8th, 2018 is a Sunday
  const startDate = new Date(2018, 6, 8);
  startDate.setDate(startDate.getDate() + firstDayOfWeek);
  const daysOfWeek = [];
  const daysOfWeekLength = 7;
  for (let i = 0; i < daysOfWeekLength; i++) {
    daysOfWeek.push(Formatters.formatNarrowDayOfWeek(getDateWithDayOffset(startDate, i)));
  }
  return daysOfWeek;
}

type ParsingFunction = typeof Parsers.parseFullDate;
export const dateParsingFunctions: Array<ParsingFunction> = [
  // stricter parsing functions first
  Parsers.parseShortDateTimeWithFullYear,
  Parsers.parseShortDateTimeWithFullYear24Hour,
  Parsers.parseShortDateMediumTimeWithFullYear,
  Parsers.parseShortDateMediumTimeWithFullYear24Hour,
  Parsers.parseShortDateTime,
  Parsers.parseMediumDateTime,
  Parsers.parseLongDateTime,
  Parsers.parseFullDateTime,
  Parsers.parseShortDate,
  Parsers.parseMediumDate,
  Parsers.parseLongDate,
  Parsers.parseFullDate,
  Parsers.parseShortDateWithFullYear,
];

export function getDateFormatter(format: DateFormat): (date: Date) => string {
  switch (format) {
    case DateFormat.ShortDateTime:
      return Formatters.formatShortDateTime;
    case DateFormat.MediumDateTime:
      return Formatters.formatMediumDateTime;
    case DateFormat.LongDateTime:
      return Formatters.formatLongDateTime;
    case DateFormat.FullDateTime:
      return Formatters.formatFullDateTime;
    case DateFormat.ShortDate:
      return Formatters.formatShortDate;
    case DateFormat.MediumDate:
      return Formatters.formatMediumDate;
    case DateFormat.LongDate:
      return Formatters.formatLongDate;
    case DateFormat.FullDate:
      return Formatters.formatFullDate;
    case DateFormat.ShortDateWithFullYear:
      return Formatters.formatShortDateWithFullYear;
    case DateFormat.ShortDateTimeWithFullYear:
      return Formatters.formatShortDateTimeWithFullYear;
    case DateFormat.ShortDateTimeWithFullYear24Hour:
      return Formatters.formatShortDateTimeWithFullYear24Hour;
    case DateFormat.ShortDateMediumTimeWithFullYear:
      return Formatters.formatShortDateMediumTimeWithFullYear;
    case DateFormat.ShortDateMediumTimeWithFullYear24Hour:
      return Formatters.formatShortDateMediumTimeWithFullYear24Hour;
    default:
      throw new Error(`Unsupported DateFormat: ${format}`);
  }
}

export function getParsedDate(text: string, terminus: DateTerminus): Date | null {
  let parsedFunction = null;
  let parsedDateTime = null;
  for (let i = 0; i < dateParsingFunctions.length; i++) {
    const dateParsingFunction = dateParsingFunctions[i];
    parsedDateTime = dateParsingFunction(text);
    if (parsedDateTime) {
      parsedFunction = dateParsingFunction;
      break;
    }
  }
  // parsedDateTime might not be a valid date object if year is too big.
  if (parsedDateTime && !isNaN(parsedDateTime.getTime())) {
    return getDateWithTerminus(parsedDateTime, parsedFunction, terminus);
  } else {
    return null;
  }
}

export function getDateWithTerminus(date: Date, parsingFunction: ParsingFunction | null, terminus: DateTerminus): Date {
  const newDate = new Date(date.getTime());
  switch (parsingFunction) {
    case Parsers.parseShortDate:
    case Parsers.parseMediumDate:
    case Parsers.parseLongDate:
    case Parsers.parseFullDate:
    case Parsers.parseShortDateWithFullYear:
      if (terminus === DateTerminus.End) {
        newDate.setHours(24, 0, 0, -1);
      } else {
        newDate.setHours(0, 0, 0, 0);
      }
      return newDate;
    // Seconds are not defined
    case Parsers.parseShortDateTimeWithFullYear:
    case Parsers.parseShortDateTimeWithFullYear24Hour:
      if (terminus === DateTerminus.End) {
        newDate.setSeconds(60, -1);
      } else {
        newDate.setSeconds(0, 0);
      }
      return newDate;
    default:
      return newDate;
  }
}

export function getISOWeekNum(date: Date): number {
  // https://stackoverflow.com/questions/6117814/get-week-of-year-in-javascript-like-in-php/6117889#6117889
  date = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  // Set to nearest Thursday: current date + 4 - current day number
  // Make Sunday's day number 7
  date.setUTCDate(date.getUTCDate() + 4 - (date.getUTCDay() || 7));
  // Get first day of year
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  // Calculate full weeks to nearest Thursday
  return Math.ceil((((date.valueOf() - yearStart.valueOf()) / 86400000) + 1) / 7);
}

export function getStartDayOfWeek(showWeekNumbers: boolean): DayOfWeek {
  return showWeekNumbers ? DayOfWeek.Mon : getDayOfWeekEnum(LocaleData.get().firstDayOfWeek);
}
